// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { RootState } from 'admin/core/store';
import { Loader } from 'common/components';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import { Translate } from '@plesk/ui-library';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import { StyledTable } from 'common/components/styles/StyledTable';
import { IFailoverDomainResponse } from 'common/api/resources/FailoverDomain';
import FailoverDomainTable from 'admin/failoverDomain/containers/FailoverDomainTable';
import FailoverDomainCreateDialog from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/FailoverDomainCreateDialog';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import { ICONS } from 'common/constants';

export type FailoverDomainsProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const FailoverDomains: React.FC<FailoverDomainsProps> = ({
    list,
    isLoadingList,
    failoverDomainActions: {
        getFailoverDomains,
        toggleActiveState,
        deleteFailoverDomain,
    },
}) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editItem, setEditItem] = React.useState<IFailoverDomainResponse | undefined>(undefined);

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.FAILOVER_DOMAINS.VISIT);
        getFailoverDomains();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = () => {
        setEditItem(undefined);
        setDialogOpen(true);
    };

    const handleEdit = (item: IFailoverDomainResponse) => {
        setEditItem(item);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditItem(undefined);
    };

    const handleDialogSaved = () => {
        setDialogOpen(false);
        setEditItem(undefined);
        getFailoverDomains();
    };

    const handleToggleActive = (item: IFailoverDomainResponse) => {
        toggleActiveState(item.id, {
            is_active: !item.is_active,
        });
    };

    const handleDelete = (item: IFailoverDomainResponse) => {
        deleteFailoverDomain(item.id);
    };

    return (
        <>
            <PageHeader
                title={<Translate content="failoverDomain.title" />}
                buttonText="failoverDomain.addBtn"
                buttonIcon={ICONS.RESOURCE}
                onButtonClick={handleCreate}
                isButtonShown={list.length > 0}
            />
            <StyledTable>
                <Loader isLoading={isLoadingList}>
                    <FailoverDomainTable
                        items={list}
                        onCreate={handleCreate}
                        onEdit={handleEdit}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDelete}
                    />
                </Loader>
            </StyledTable>
            {dialogOpen && (
                <FailoverDomainCreateDialog
                    editItem={editItem}
                    onClose={handleDialogClose}
                    onSaved={handleDialogSaved}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    list: state.failoverDomain.data,
    isLoadingList: !state.failoverDomain.data.length
        && state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_LIST),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    failoverDomainActions: bindActionCreators(failoverDomainActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(FailoverDomains);
